import { useState } from 'react';
import { clsx } from 'clsx';
import { ConfirmDialog, useToast } from '@centraoneplus/ui';
import {
  downloadSkippedClients,
  errorMessage,
  huhokaApi,
  isSyncInProgress,
  useHuhokaDisconnect,
  useHuhokaResync,
  useHuhokaStatus,
} from './api';
import { CARD_BADGE, cardState, formatWhen, syncSummary, type CardState } from './huhoka';

const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';
const ACTION =
  'co-mono text-[9.5px] font-semibold uppercase tracking-[.1em] hover:underline disabled:opacity-50 disabled:no-underline';

const BADGE_STYLE: Record<CardState, React.CSSProperties> = {
  connected: {
    borderColor: 'var(--co-green-line)',
    background: 'var(--co-green-wash)',
    color: 'var(--co-green)',
  },
  error: {
    borderColor: 'var(--co-burgundy-line)',
    background: 'var(--co-burgundy-wash)',
    color: 'var(--co-burgundy)',
  },
  pending: {
    borderColor: 'var(--co-terracotta-line)',
    background: 'var(--co-terracotta-wash)',
    color: 'var(--co-terracotta)',
  },
  disconnected: {
    borderColor: 'var(--co-line-strong)',
    background: 'var(--co-selected)',
    color: 'var(--co-ink-3)',
  },
  unconfigured: {
    borderColor: 'var(--co-line-strong)',
    background: 'var(--co-selected)',
    color: 'var(--co-ink-3)',
  },
};

/**
 * Integrations -> HuHoKa CRM (plan 14, Phases 1-2). Rendered for tenant admins
 * only; the BFF and integration-hub enforce the same rule.
 *
 *   Disconnected  description + Connect
 *   Pending       waiting for approval in HuHoKa CRM + Start again / Cancel
 *   Connected     organisation, connected by/on, client sync (state, synced count,
 *                 N skipped + CSV download), last sync, Re-sync now, Disconnect (confirm)
 *   Error         the reason + Reconnect
 */
export function HuhokaCrmCard({ tenantId }: { tenantId?: string }) {
  const toast = useToast();
  const { data: status, isLoading, isError, refetch } = useHuhokaStatus(tenantId);
  const disconnect = useHuhokaDisconnect(tenantId);
  const resync = useHuhokaResync(tenantId);
  const [connecting, setConnecting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const state = cardState(status);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { authorizeUrl } = await huhokaApi.connect();
      // Full navigation: the admin signs in to HuHoKa CRM and approves there.
      window.location.assign(authorizeUrl);
    } catch (err) {
      toast.error('HuHoKa CRM', errorMessage(err, 'The connection could not be started.'));
      setConnecting(false);
      await refetch();
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync();
      toast.success('HuHoKa CRM', 'Disconnected. Both products keep their data.');
    } catch (err) {
      toast.error('HuHoKa CRM', errorMessage(err, 'The connection could not be closed. Try again.'));
    } finally {
      setConfirmOpen(false);
    }
  };

  const handleResync = async () => {
    try {
      await resync.mutateAsync();
      toast.success('HuHoKa CRM', 'Re-sync started. Clients update in the background.');
    } catch (err) {
      toast.error('HuHoKa CRM', errorMessage(err, 'Re-sync could not be started.'));
    }
  };

  const handleDownloadSkipped = async () => {
    setDownloading(true);
    try {
      await downloadSkippedClients();
    } catch (err) {
      toast.error('HuHoKa CRM', errorMessage(err, 'The list of skipped clients could not be downloaded.'));
    } finally {
      setDownloading(false);
    }
  };

  const syncing = isSyncInProgress(status);

  return (
    <div className={clsx(CARD_CLASS, 'flex flex-col px-[18px] py-4')} style={CARD} data-testid="huhoka-crm-card">
      <div className="flex items-start gap-3">
        <div
          className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg border text-[12px] font-bold"
          style={
            state === 'connected'
              ? { borderColor: 'var(--co-brass)', background: 'var(--co-brass-wash)', color: 'var(--co-brass-ink)' }
              : { borderColor: 'var(--co-line)', background: 'var(--co-selected)', color: 'var(--co-ink-3)' }
          }
          aria-hidden
        >
          HK
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
            HuHoKa CRM
          </div>
          <div className="co-mono mt-1 truncate text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
            CRM - clients and leads
          </div>
        </div>
      </div>

      <div className="mt-[13px]">
        <span
          className="co-mono inline-flex items-center whitespace-nowrap rounded-[5px] border px-2 py-[3px] text-[9.5px] font-semibold uppercase tracking-[.1em]"
          style={BADGE_STYLE[state]}
          data-testid="huhoka-status-badge"
        >
          {isLoading ? 'Checking' : CARD_BADGE[state]}
        </span>
      </div>

      <div className="mt-[10px] flex-1 text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
        {isError ? (
          <p>The connection status could not be read.</p>
        ) : state === 'connected' && status ? (
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
            <dt>Organisation</dt>
            <dd className="truncate text-[var(--co-ink-2)]">
              {status.remoteWebUrl ? (
                <a href={status.remoteWebUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                  {status.remoteTenantName ?? 'HuHoKa CRM'}
                </a>
              ) : (
                (status.remoteTenantName ?? 'HuHoKa CRM')
              )}
            </dd>
            <dt>Connected by</dt>
            <dd className="truncate text-[var(--co-ink-2)]">{status.connectedByName ?? '-'}</dd>
            <dt>Connected on</dt>
            <dd className="text-[var(--co-ink-2)]">{formatWhen(status.connectedAt)}</dd>
            <dt>Client sync</dt>
            <dd
              className={clsx(
                'truncate',
                status.syncState === 'failed' ? 'text-[var(--co-burgundy)]' : 'text-[var(--co-ink-2)]',
              )}
              data-testid="huhoka-sync-state"
            >
              {syncSummary(status)}
            </dd>
            <dt>Customers</dt>
            <dd className="text-[var(--co-ink-2)]" data-testid="huhoka-synced-count">
              {status.syncedCount.toLocaleString()} synced
              {status.skippedCount > 0 && (
                <>
                  {', '}
                  <button
                    type="button"
                    onClick={handleDownloadSkipped}
                    disabled={downloading}
                    className="text-[var(--co-terracotta)] hover:underline disabled:opacity-50"
                    title="Download the clients that could not be synced (CSV)"
                    data-testid="huhoka-skipped"
                  >
                    {status.skippedCount.toLocaleString()} skipped
                  </button>
                </>
              )}
            </dd>
            <dt>Last sync</dt>
            <dd className="text-[var(--co-ink-2)]">{status.lastSyncAt ? formatWhen(status.lastSyncAt) : 'Not yet'}</dd>
          </dl>
        ) : state === 'pending' ? (
          <p>
            Waiting for approval in HuHoKa CRM
            {status?.remoteTenantName ? ` (${status.remoteTenantName})` : ''}. This card updates by itself once the
            connection is approved.
          </p>
        ) : state === 'error' ? (
          <p className="text-[var(--co-burgundy)]" data-testid="huhoka-error">
            {status?.lastError ?? 'The connection failed.'}
          </p>
        ) : state === 'unconfigured' ? (
          <p>HuHoKa CRM is not set up for this environment yet. Contact your platform administrator.</p>
        ) : (
          <p>
            Link this workspace to your HuHoKa CRM organisation. Clients sync here as customers, and HuHoKa CRM shows
            their tickets and history.
          </p>
        )}
      </div>

      <div className="mt-[13px] flex items-center gap-3 border-t border-[var(--co-line-subtle)] pt-3">
        {state === 'disconnected' && (
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting || isLoading}
            className={clsx(ACTION, 'text-[var(--co-brass-ink)]')}
          >
            {connecting ? 'Connecting...' : 'Connect'}
          </button>
        )}
        {state === 'error' && (
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            className={clsx(ACTION, 'text-[var(--co-brass-ink)]')}
          >
            {connecting ? 'Connecting...' : 'Reconnect'}
          </button>
        )}
        {state === 'pending' && (
          <>
            {status?.role === 'initiator' && (
              <button
                type="button"
                onClick={handleConnect}
                disabled={connecting}
                className={clsx(ACTION, 'text-[var(--co-brass-ink)]')}
              >
                Start again
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className={clsx(ACTION, 'text-[var(--co-ink-3)]')}
            >
              Cancel
            </button>
          </>
        )}
        {state === 'connected' && (
          <>
            <button
              type="button"
              onClick={handleResync}
              disabled={resync.isPending || syncing}
              className={clsx(ACTION, 'text-[var(--co-brass-ink)]')}
            >
              {resync.isPending || syncing ? 'Syncing...' : 'Re-sync now'}
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className={clsx(ACTION, 'text-[var(--co-burgundy)]')}
            >
              Disconnect
            </button>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDisconnect}
        loading={disconnect.isPending}
        title={state === 'pending' ? 'Cancel the HuHoKa CRM connection?' : 'Disconnect HuHoKa CRM?'}
        message={
          state === 'pending'
            ? 'The pending connection request will stop working.'
            : 'HuHoKa CRM will no longer be able to reach this workspace and syncing stops. Customers and tickets already here stay, and synced customers become editable again.'
        }
        confirmLabel={state === 'pending' ? 'Cancel connection' : 'Disconnect'}
        cancelLabel="Keep"
        variant="danger"
      />
    </div>
  );
}

export default HuhokaCrmCard;
